import { toast } from "sonner";
import { ProgressToast } from "../components/progress-toast";

interface ProgressToastHandle {
  /** Update progress (0-100). Pass a message to change the label. */
  update: (progress: number, message?: string) => void;
  /** Transition to success state. Toast auto-dismisses after default duration. */
  success: (message: string) => void;
  /** Transition to error state. Toast auto-dismisses after default duration. */
  error: (message: string) => void;
  /** Dismiss the toast immediately. */
  dismiss: () => void;
}

/**
 * Create a persistent progress toast for downloads/exports.
 *
 * @param message - Initial message shown in the toast
 * @param options.indeterminate - Show indeterminate progress bar (no percentage)
 * @returns Handle to update, complete, or dismiss the toast
 *
 * @example
 * ```ts
 * const progress = createProgressToast("Downloading...");
 * progress.update(50);
 * progress.success("Download complete");
 * ```
 *
 * @example
 * ```ts
 * // Indeterminate mode for operations without progress tracking
 * const progress = createProgressToast("Exporting CSV...", { indeterminate: true });
 * progress.success("Export complete");
 * ```
 */
export function createProgressToast(message: string, options: { indeterminate?: boolean } = {}): ProgressToastHandle {
  let isIndeterminate = options.indeterminate ?? false;
  let currentMessage = message;

  const id = toast.custom(
    () => <ProgressToast message={currentMessage} progress={0} indeterminate={isIndeterminate} />,
    { duration: Infinity }
  );

  return {
    update(progress: number, newMessage?: string) {
      if (newMessage) currentMessage = newMessage;
      // Switch to determinate mode once meaningful progress is reported
      if (progress > 0) isIndeterminate = false;
      toast.custom(
        () => <ProgressToast message={currentMessage} progress={progress} indeterminate={isIndeterminate} />,
        { id, duration: Infinity }
      );
    },
    success(successMessage: string) {
      // sonner v2 retains the `jsx` from the prior toast.custom call when a
      // typed toast reuses the same id, suppressing the success title. Dismiss
      // the custom toast and create a fresh one so the success state renders.
      toast.dismiss(id);
      toast.success(successMessage, { duration: 4000 });
    },
    error(errorMessage: string) {
      toast.dismiss(id);
      toast.error(errorMessage, { duration: 4000 });
    },
    dismiss() {
      toast.dismiss(id);
    }
  };
}
